import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { TabTile } from "@/components/TabTile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sun,
  Moon,
  ExternalLink,
  LayoutGrid,
  List,
  Search,
  Rocket,
  Star,
  Loader2,
  Copy
} from "lucide-react";
import { useTheme } from "next-themes";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import PageFooter from "@/components/PageFooter";

// Extended TabData to match backend structure
export interface CollectionTabData {
  id: string;
  url: string;
  title: string;
  description?: string;
  thumbnail?: string;
  favIconUrl?: string;
  ogImage?: string;
}

interface CollectionData {
  id: string;
  timestamp: string;
  tabCount: number;
  tabs: CollectionTabData[];
  updatedAt?: string;
}

// Fetch collection from backend
async function fetchCollection(id: string): Promise<CollectionData> {
  const response = await fetch(`/api/collections/${id}`);
  if (!response.ok) {
    const error = new Error('Collection not found');
    (error as any).status = response.status;
    throw error;
  }
  const data = await response.json();

  // Ensure each tab has a unique id
  return {
    ...data,
    tabs: data.tabs.map((tab: any, index: number) => ({
      ...tab,
      id: tab.id || `${tab.url}-${index}`,
      description: tab.description || 'No description available',
    })),
  };
}

// Update collection on backend
async function updateCollection(id: string, tabs: CollectionTabData[]): Promise<void> {
  const editToken = localStorage.getItem(`editToken_${id}`);
  if (!editToken) {
    throw new Error('Missing edit token for this collection');
  }

  const response = await fetch(`/api/collections/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Edit-Token': editToken,
    },
    body: JSON.stringify({ tabs }),
  });

  if (!response.ok) {
    throw new Error('Failed to save changes');
  }
}

// Format timestamp
function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

const Collection = () => {
  const { id } = useParams<{ id: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [tabToRemove, setTabToRemove] = useState<string | null>(null);
  const [isMinimalistic, setIsMinimalistic] = useState(false);
  const [highlightedTabs, setHighlightedTabs] = useState<Set<string>>(new Set());
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const { resolvedTheme, setTheme} = useTheme();
  const queryClient = useQueryClient();


  // Fetch collection data - no polling needed with real-time
  const { data: collection, isLoading, error } = useQuery({
    queryKey: ['collection', id],
    queryFn: () => fetchCollection(id!),
    enabled: !!id,
    staleTime: 0, // Always consider data stale for immediate refetches
    refetchOnMount: true,
    retry: (failureCount, error: Error & { status?: number }) => {
      // Don't retry on 404 (collection not found)
      if (error?.status === 404) {
        return false;
      }
      // Retry up to 2 times for transient errors (500, 503, network issues, etc.)
      return failureCount < 2;
    },
    retryDelay: 1000, // 1 second between retries instead of exponential backoff
  });

  // Prevent search engine indexing of shared collections
  useEffect(() => {
    // Add robots meta tag to prevent indexing
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      // Cleanup on unmount
      document.head.removeChild(meta);
    };
  }, []);

  // Check if user has edit access (edit token stored in localStorage)
  useEffect(() => {
    if (!id) return;

    // Check immediately on mount
    const editToken = localStorage.getItem(`editToken_${id}`);
    setHasEditAccess(!!editToken);

    // Check again after a short delay to catch extension token injection
    const timeoutId = setTimeout(() => {
      const editToken = localStorage.getItem(`editToken_${id}`);
      setHasEditAccess(!!editToken);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [id]);

  // Real-time subscription to collection updates
  useEffect(() => {
    if (!id) return;

    console.log('[Realtime] Setting up subscription for collection:', id);

    const channel = supabase
      .channel(`collection-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'api',
          table: 'collections',
          filter: `id=eq.${id}`
        },
        async (payload) => {
          console.log('[Realtime] UPDATE event received:', payload);
          // Immediately fetch fresh data and update cache
          try {
            const freshData = await fetchCollection(id);
            queryClient.setQueryData(['collection', id], freshData);
            console.log('[Realtime] Data updated, tab count:', freshData.tabs.length);
          } catch (error) {
            console.error('[Realtime] Error fetching fresh data:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Subscription status:', status);
      });

    return () => {
      console.log('[Realtime] Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [id, queryClient]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (tabs: CollectionTabData[]) => updateCollection(id!, tabs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection', id] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    },
  });

  // Filter tabs based on search
  const filteredTabs = collection?.tabs.filter((tab) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      tab.title.toLowerCase().includes(query) ||
      tab.url.toLowerCase().includes(query) ||
      (tab.description || '').toLowerCase().includes(query)
    );
  }) || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!collection || !over || active.id === over.id) return;

    // Get the dragged and drop target tabs from filtered view
    const draggedTab = filteredTabs.find((t) => t.id === active.id);
    const dropTargetTab = filteredTabs.find((t) => t.id === over.id);

    if (!draggedTab || !dropTargetTab) return;

    // Find indices in full collection
    const draggedIndexInFull = collection.tabs.findIndex((t) => t.id === draggedTab.id);
    const dropTargetIndexInFull = collection.tabs.findIndex((t) => t.id === dropTargetTab.id);

    if (draggedIndexInFull === -1 || dropTargetIndexInFull === -1) return;

    // Reorder in full collection using arrayMove
    const updatedTabs = arrayMove(
      collection.tabs,
      draggedIndexInFull,
      dropTargetIndexInFull
    );

    // Update locally and on backend
    queryClient.setQueryData(['collection', id], {
      ...collection,
      tabs: updatedTabs,
    });

    updateMutation.mutate(updatedTabs);

    toast({
      title: "Order updated",
      description: "Tab order has been saved",
    });
  };

  const handleRemove = (tabId: string) => {
    setTabToRemove(tabId);
  };

  const confirmRemove = () => {
    if (!collection || !tabToRemove) return;

    const updatedTabs = collection.tabs.filter((tab) => tab.id !== tabToRemove);

    queryClient.setQueryData(['collection', id], {
      ...collection,
      tabs: updatedTabs,
      tabCount: updatedTabs.length,
    });

    updateMutation.mutate(updatedTabs);
    setTabToRemove(null);

    toast({
      title: "Tab deleted",
      description: "The tab has been removed from your collection.",
    });
  };

  const handleOpenAll = async () => {
    // Use filtered tabs to respect search query
    const tabsToOpen = filteredTabs.length > 0 ? filteredTabs : collection?.tabs || [];

    if (tabsToOpen.length === 0) {
      toast({
        title: "No tabs",
        description: "No tabs to open",
      });
      return;
    }

    let successCount = 0;
    let failedCount = 0;

    // Open tabs in background with visual feedback
    for (let i = 0; i < tabsToOpen.length; i++) {
      const tab = tabsToOpen[i];

      // Highlight tab before opening
      setHighlightedTabs(prev => new Set([...prev, tab.id]));

      try {
        // Open tab in new window (background)
        const opened = window.open(tab.url, '_blank', 'noopener,noreferrer');

        if (opened) {
          successCount++;
        } else {
          failedCount++;
        }
      } catch (error) {
        failedCount++;
      }

      // Remove highlight after animation (1 second)
      setTimeout(() => {
        setHighlightedTabs(prev => {
          const next = new Set(prev);
          next.delete(tab.id);
          return next;
        });
      }, 1000);

      // Add delay between opens (200ms) to help with popup blocking and visual effect
      if (i < tabsToOpen.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // Show results
    if (failedCount === 0) {
      toast({
        title: "Tabs opened",
        description: `Successfully opened ${successCount} tab${successCount === 1 ? '' : 's'}`,
      });
    } else if (successCount === 0) {
      toast({
        title: "Popup blocked",
        description: "Please allow popups for this site and try again by clicking 'Open All'.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Partial success",
        description: `${successCount} tabs opened, ${failedCount} blocked. Allow popups and click 'Open All' again to open remaining tabs.`,
      });
    }
  };

  const handleExportBookmarks = () => {
    if (!collection || collection.tabs.length === 0) {
      toast({
        title: "No tabs",
        description: "No tabs to export",
      });
      return;
    }

    const html = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file. -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>Bookmarks</TITLE>
<H1>Bookmarks</H1>
<DL><p>
    <DT><H3>Untabbed Collection - ${formatTime(collection.timestamp)}</H3>
    <DL><p>
${collection.tabs.map(tab => `        <DT><A HREF="${tab.url}">${tab.title || 'Untitled'}</A>`).join('\n')}
    </DL><p>
</DL><p>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `untabbed-collection-${id}.html`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Bookmarks exported",
      description: "Import the downloaded file in your browser's bookmark manager.",
    });
  };

  const handleDuplicate = async () => {
    if (!collection) return;

    try {
      // Create a new collection with the same tabs
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://untabbed.to'}/api/collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tabs: collection.tabs,
          timestamp: new Date().toISOString(),
          tabCount: collection.tabs.length,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate collection');
      }

      const result = await response.json();

      // Store the edit token in localStorage so the new tab has edit access
      if (result.editToken && result.id) {
        localStorage.setItem(`editToken_${result.id}`, result.editToken);
      }

      toast({
        title: "Collection duplicated",
        description: "Opening the new collection...",
      });

      // Open the new collection in a new tab
      window.open(`/collection/${result.id}`, '_blank');
    } catch (error) {
      console.error('Error duplicating collection:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate collection",
        variant: "destructive",
      });
    }
  };

  // Handle card click to open URL
  const handleCardClick = (tabId: string) => {
    const tab = collection?.tabs.find((t) => t.id === tabId);
    if (tab) {
      window.open(tab.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your collection...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !collection) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <p className="text-4xl mb-4">⚠️</p>
          <h2 className="text-2xl font-bold mb-2">Error</h2>
          <p className="text-muted-foreground">Collection not found</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalTabs = collection.tabCount;
  const visibleTabs = filteredTabs.length;
  const statsText = totalTabs === visibleTabs
    ? `${totalTabs}`
    : `${visibleTabs} of ${totalTabs}`;

  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col">
      {/* Header */}
      <header className="glass-card border-b border-border/30 sticky top-0 z-10 shadow-elegant">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img src={logoDark} alt="untabbed.to logo" className="h-8 w-8 dark:hidden" />
              <img src={logo} alt="untabbed.to logo" className="h-8 w-8 hidden dark:block" />
              <Link to="/">
                <h1 className="text-3xl font-display font-bold text-black dark:text-white tracking-tight hover:opacity-80 transition-opacity">
                  untabbed.to
                </h1>
              </Link>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search tabs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="default"
                size="sm"
                onClick={handleOpenAll}
                className="shadow-glow"
              >
                <Rocket className="h-4 w-4 mr-2" />
                Open All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportBookmarks}
              >
                <Star className="h-4 w-4 mr-2" />
                Bookmarks
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMinimalistic(!isMinimalistic)}
              >
                {isMinimalistic ? (
                  <LayoutGrid className="h-4 w-4 mr-2" />
                ) : (
                  <List className="h-4 w-4 mr-2" />
                )}
                {isMinimalistic ? "Grid" : "Minimal"}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                title={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {resolvedTheme === "dark" ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      {!isMinimalistic && (
        <div className="container mx-auto px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="glass-card p-4 rounded-xl shadow-elegant">
              <div className="text-sm text-muted-foreground mb-1">Tabs</div>
              <div className="text-3xl font-bold text-primary">{statsText}</div>
            </div>
            <div className="glass-card p-4 rounded-xl shadow-elegant">
              <div className="text-sm text-muted-foreground mb-1">Captured</div>
              <div className="text-3xl font-bold text-primary">
                {formatTime(collection.timestamp)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 pb-8 flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={filteredTabs.map(tab => tab.id)} strategy={rectSortingStrategy}>
            <div className={isMinimalistic ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"}>
              {filteredTabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => handleCardClick(tab.id)}
                  className={`transition-all duration-500 ${
                    highlightedTabs.has(tab.id)
                      ? 'animate-pulse ring-4 ring-primary/50 shadow-[0_0_30px_rgba(59,130,246,0.5)] scale-105'
                      : ''
                  }`}
                >
                  <TabTile
                    tab={tab}
                    onRemove={handleRemove}
                    minimalistic={isMinimalistic}
                    editable={hasEditAccess}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {filteredTabs.length === 0 && searchQuery && (
          <div className="text-center py-20">
            <p className="text-6xl mb-4">🔍</p>
            <h3 className="text-2xl font-bold mb-2">No tabs found</h3>
            <p className="text-muted-foreground text-lg">
              Try adjusting your search query
            </p>
          </div>
        )}

        {collection.tabs.length === 0 && !searchQuery && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">
                Loading tabs...
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <PageFooter />

      {/* Confirmation Dialog */}
      <AlertDialog open={!!tabToRemove} onOpenChange={() => setTabToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this tab?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The tab will be permanently removed from your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Collection;
